import { useEffect, useRef, useState } from "react";
import { LuChevronLeft, LuX } from "react-icons/lu";
import blueWave from "../../assets/blue_wave.mp4";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import FeedbackForm from "./FeedbackForm";

export enum FeedbackType {
  BUG = "bug",
  FEATURE = "feature",
}

export default function FeedbackModal({ onClose }: { onClose?: () => void }) {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedType, setSelectedType] = useState<FeedbackType | null>(null);
  const closeTimeout = useRef<NodeJS.Timeout | null>(null);
  const isCompactView = useIsCompactView();

  useEffect(() => {
    // Prevent background scroll when modal is open
    document.body.style.overflow = "hidden";
    setIsVisible(true);

    return () => {
      document.body.style.overflow = "";
      if (closeTimeout.current) clearTimeout(closeTimeout.current);
    };
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    closeTimeout.current = setTimeout(() => {
      onClose?.();
    }, 200); // match transition duration
  };

  return (
    <div
      className={`${
        isVisible ? "opacity-100" : "opacity-0 pointer-events-none"
      } fixed inset-0 z-50 flex items-center justify-center
      transition-opacity duration-300
      bg-black/50 backdrop-blur-sm`}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        className={`relative shadow-2xl
            bg-light-surface-primary dark:bg-[#1B1E22]
            overflow-hidden
          ${
            isCompactView
              ? "w-full h-full"
              : "w-[90%] max-w-2xl h-[70%] max-h-[500px] rounded-3xl"
          }`}
      >
        <div className="flex flex-col w-full h-full">
          <div className="h-[75px] relative overflow-hidden">
            <video src={blueWave} autoPlay loop muted playsInline />
            <div
              className="absolute inset-0 bg-gradient-to-t 
              from-light-surface-primary dark:from-[#1B1E22] 
              to-transparent pointer-events-none"
            />
          </div>
          {selectedType ? (
            <FeedbackForm type={selectedType} />
          ) : (
            <Selection onSelect={setSelectedType} />
          )}
        </div>
        <button
          onClick={handleClose}
          className="absolute top-4 right-4
          text-[#E0E3F2]
        hover:opacity-50 transition-opacity duration-300"
        >
          <LuX size={24} />
        </button>
        {selectedType && (
          <button
            onClick={() => setSelectedType(null)}
            className="absolute top-4 left-4
          text-[#E0E3F2]
        hover:opacity-50 transition-opacity duration-300"
          >
            <LuChevronLeft size={24} />
          </button>
        )}
      </div>
    </div>
  );
}

function Selection({ onSelect }: { onSelect: (type: FeedbackType) => void }) {
  const isCompactView = useIsCompactView();

  return (
    <div
      className="pt-[50px]
    flex flex-col items-center gap-10"
    >
      <p className="text-2xl font-semibold">What would you like to do?</p>
      <div className={`flex gap-7 ${isCompactView && "flex-col"}`}>
        <Btn
          id="feedback-modal-report-bug"
          label="Report a bug"
          onClick={() => onSelect(FeedbackType.BUG)}
        />
        <Btn
          id="feedback-modal-request-feature"
          label="Request feature"
          onClick={() => onSelect(FeedbackType.FEATURE)}
        />
      </div>
    </div>
  );
}

function Btn({
  id,
  label,
  onClick,
}: {
  id: string;
  label: string;
  onClick: () => void;
}) {
  const isCompactView = useIsCompactView();

  return (
    <button
      id={id}
      onClick={onClick}
      className={`border border-[#373B404D] dark:border-[#373B40]
    p-2 rounded-lg
    hover:bg-primary-blue hover:text-white
    transition-all duration-300 ${
      isCompactView ? "w-[300px] h-[75px] text-xl" : "w-[200px]"
    }`}
    >
      {label}
    </button>
  );
}
